<template>
    <vx-table-row>
        <vx-table-row-title class="break-words">
            {{ model.id === userId ? 'You' : model.name }}
            <div class="text-sm text-slate-500 font-normal mt-1">{{ model.email }}</div>
        </vx-table-row-title>

        <vx-table-row-item v-if="FEATURES.contact_groups">
            <template v-slot:label> Target Access: </template>

            <div class="flex items-center gap-1">
                <vx-popover v-if="model.role != 'none'" hover arrow placement="top">
                    <meta-badge :icon="faCrown" color="amber" data-test="corp-badge"> </meta-badge>
                    <template v-slot:content> Corporate Access </template>
                </vx-popover>
                <vx-popover hover arrow placement="top">
                    <meta-badge :icon="faBuildingFlag" color="violet" data-test="ou-badge">
                        {{ orgUnitCount }}
                    </meta-badge>
                    <template v-slot:content> Org Unit Access </template>
                </vx-popover>

                <vx-popover hover arrow placement="top">
                    <meta-badge :icon="faGrid2Plus" color="slate" data-test="group-badge">
                        {{ groupCount }}
                    </meta-badge>
                    <template v-slot:content> Group Access </template>
                </vx-popover>
            </div>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Team Role: </template>
            <vx-selectable
                v-if="isEditable"
                :modelValue="model.role"
                @update:modelValue="onRoleChange"
                :reduce="(option) => option.value"
                :options="teamRolesOptions({ exclude: ['owner'] })"
                :clearable="false"
                :disabled="updating"
                data-test="role-select"
                class="w-[240px]"
            />
            <template v-else>
                {{ TEAM_USER_ROLES[model.role] || '-' }}
            </template>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="model.role !== 'owner' && model.id !== userId && isAdminOrOwner && !props.readOnly">
            <vx-dropdown placement="right">
                <vx-button data-test="dropdown" type="button" color="muted" outline class="w-[45px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item
                        v-if="FEATURES.contact_groups"
                        data-test="add-to-org-unit"
                        @click="emit('addToOrgUnit', model)"
                    >
                        Add to OU
                        <font-awesome-icon widthAuto :icon="faBuildingFlag"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item
                        v-if="FEATURES.contact_groups"
                        data-test="add-to-group"
                        @click="emit('addToGroup', model)"
                    >
                        Add to Group
                        <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item data-test="delete" @click="confirmModal = true">
                        Remove from Team
                        <font-awesome-icon widthAuto :icon="faCircleMinus"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button v-if="!props.readOnly" disabled data-test="edit" color="muted" outline>
                Edit <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>

        <warning-modal
            v-model:visible="confirmModal"
            confirmText="Yes, remove user"
            message="The user will immediately lose access to this team."
            type="danger"
            @confirm="deleteUser"
        >
        </warning-modal>
    </vx-table-row>
</template>

<script setup>
import {
    faBuildingFlag,
    faCircleMinus,
    faCrown,
    faEllipsis,
    faFilePen,
    faGrid2Plus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxPopover,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxSelectable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { TEAM_USER_ROLES, teamRolesOptions } from '~/constants/roles';
import { usersClient } from '~/services';
import MetaBadge from '~/components/general/MetaBadge.vue';
import { computed, inject, ref } from 'vue';
import WarningModal from '~/components/modals/WarningModal.vue';
import { catchRequestError } from '~/utils/request';
import { formatNumber } from '~/utils/helpers';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
});

const toasted = useToasted();
const userId = Spark.state.user.id;

const isAdminOrOwner = Spark.isAdminOrOwner;
const FEATURES = inject('FEATURES');

const model = defineModel({
    type: Object,
    required: true,
});

const confirmModal = ref(false);
const updating = ref(false);
let activeToastId = null;

const emit = defineEmits(['deleted', 'addToGroup', 'addToOrgUnit']);

const isEditable = computed(
    () => model.value.role !== 'owner' && model.value.id !== userId && isAdminOrOwner && !props.readOnly
);

const orgUnitCount = computed(() =>
    ['admin', 'owner'].includes(model.value.role) ? 'All' : formatNumber(model.value.org_unit_count)
);

const groupCount = computed(() =>
    ['admin', 'owner'].includes(model.value.role) ? 'All' : formatNumber(model.value.group_count)
);

const onRoleChange = async (newRole) => {
    if (newRole === model.value.role || updating.value) {
        return;
    }

    const previousRole = model.value.role;
    updating.value = true;

    try {
        await usersClient.updateUser(props.teamId, model.value.id, { role: newRole });
        model.value.role = newRole;

        if (activeToastId !== null) {
            toasted.dismiss(activeToastId);
        }

        const toastConfig = {
            title: `${model.value.name || model.value.email} role updated to ${TEAM_USER_ROLES[newRole]}`,
            type: 'success',
            action: {
                label: 'Undo',
                onClick: async () => {
                    toasted.dismiss(activeToastId);
                    activeToastId = null;
                    try {
                        await usersClient.updateUser(props.teamId, model.value.id, { role: previousRole });
                        model.value.role = previousRole;
                    } catch (e) {
                        toasted.error(catchRequestError(e));
                    }
                },
            },
        };
        activeToastId = toasted.show(toastConfig);
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        updating.value = false;
    }
};

const deleteUser = async () => {
    try {
        await usersClient.deleteUser(props.teamId, model.value.id);
        emit('deleted', model.value.id);
        toasted.success(`${model.value.name || model.value.email} removed from Team`);
    } catch (e) {
        toasted.error(catchRequestError(e));
    }
};
</script>
